import i18n from "./i18n";

const locale = () => (i18n.language || "en").startsWith("es") ? "es-ES" : "en-US";

export const fmtDate = (d, opts) => new Date(d).toLocaleDateString(locale(), opts);
export const fmtTime = (d, opts) => new Date(d).toLocaleTimeString(locale(), opts);
export const fmtDateTime = (d, opts) => new Date(d).toLocaleString(locale(), opts);

// Local calendar-day helpers — never derive app dates via toISOString (UTC
// conversion shifts the day in US timezones) and never parse a date-only
// string with new Date() (parsed as UTC midnight, same off-by-one).
export const localDayKey = (d = new Date()) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export const parseLocalDay = (s) => {
  const [y, m, d] = String(s || "").slice(0, 10).split("-").map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};
export const fmtNum = (n, opts) => Number(n).toLocaleString(locale(), opts);
export const fmtMoney = (cents, currency = "USD") =>
  new Intl.NumberFormat(locale(), { style: "currency", currency }).format((cents || 0) / 100);

// Project-local time display: "September 4, 2026 at 7:04 AM EDT"
export const fmtTz = (isoUtc, tz) => {
  if (!isoUtc) return "";
  try {
    const d = new Date(isoUtc);
    const date = new Intl.DateTimeFormat(locale(), { timeZone: tz, year: "numeric", month: "long", day: "numeric" }).format(d);
    const time = new Intl.DateTimeFormat(locale(), { timeZone: tz, hour: "numeric", minute: "2-digit", timeZoneName: "short" }).format(d);
    return `${date} at ${time}`;
  } catch {
    return fmtDateTime(isoUtc);
  }
};

// Money tracking: "Oct 25, 8:07 PM" in the company timezone (12-hour).
export const fmtTzShort = (isoUtc, tz) => {
  if (!isoUtc) return "";
  try {
    const d = new Date(isoUtc);
    const date = new Intl.DateTimeFormat(locale(), { timeZone: tz || undefined, month: "short", day: "numeric" }).format(d);
    const time = new Intl.DateTimeFormat(locale(), { timeZone: tz || undefined, hour: "numeric", minute: "2-digit" }).format(d);
    return `${date}, ${time}`;
  } catch {
    return fmtDateTime(isoUtc);
  }
};

// Company-timezone calendar day key ("2026-10-25") for date grouping.
export const tzDayKey = (isoUtc, tz) => {
  if (!isoUtc) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone: tz || undefined, year: "numeric", month: "2-digit", day: "2-digit" }).format(new Date(isoUtc));
  } catch {
    return String(isoUtc).slice(0, 10);
  }
};

export const wxCondition = (w) => {
  if (!w) return "";
  if (w.code != null) {
    const key = `wx_${w.code}`;
    if (i18n.exists(key)) return i18n.t(key);
  }
  return w.condition || "";
};

// --- Photo timestamps: never render "Invalid Date" ---
// Single display rule: a photo's capture instant is ALWAYS shown in the
// jobsite/account timezone using the server's canonical taken_at string
// ("Sep 24, 2026 · 4:54 AM EDT"). It is never re-parsed into the browser's
// timezone — we only split the preformatted parts. taken_at_iso (UTC ISO,
// guaranteed on new uploads and backfilled) remains the sort/group key;
// created_at is the upload-time fallback.
export const safeDate = (d) => {
  if (!d) return null;
  const dt = new Date(d);
  return Number.isNaN(dt.getTime()) ? null : dt;
};

export const photoTs = (p) => {
  for (const c of [p?.taken_at_iso, p?.device_capture_ts, p?.created_at]) {
    const dt = safeDate(c);
    if (dt) return dt;
  }
  return null;
};

// Canonical jobsite-time display parts from the server's taken_at string.
const takenParts = (p) => {
  const ta = p?.taken_at || "";
  const i = ta.indexOf(" · ");
  return i > 0 ? { date: ta.slice(0, i), time: ta.slice(i + 3) } : null;
};

export const fmtPhotoDate = (p, opts) => {
  const tp = takenParts(p);
  if (tp) return tp.date;
  const dt = photoTs(p);
  return dt ? dt.toLocaleDateString(locale(), opts) : i18n.t("photo_date_unavailable");
};

export const fmtPhotoTime = (p, opts) => {
  const tp = takenParts(p);
  if (tp) return tp.time;
  const dt = photoTs(p);
  return dt ? dt.toLocaleTimeString(locale(), opts) : "";
};

// Local-day grouping key (YYYY-MM-DD in the user's timezone); "unknown" when
// no valid timestamp exists so all undated photos land in ONE group.
export const photoDayKey = (p) => {
  if (p?.day_key) return p.day_key; // server-computed company-local day (Group C1)
  const dt = photoTs(p);
  if (!dt) return "unknown";
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

// Format a photoDayKey back to a local date label (parse as LOCAL midnight,
// not UTC, to avoid off-by-one day shifts).
export const fmtDayKey = (key) => {
  if (!key || key === "unknown") return i18n.t("photo_date_unavailable");
  const [y, m, d] = String(key).split("-").map(Number);
  const dt = new Date(y, (m || 1) - 1, d || 1);
  return Number.isNaN(dt.getTime()) ? i18n.t("photo_date_unavailable") : dt.toLocaleDateString(locale(), { year: "numeric", month: "short", day: "numeric" });
};

// US phone display: (740) 837-1758. Stored value is never modified.
export const fmtPhone = (p) => {
  const d = String(p || "").replace(/\D/g, "");
  const ten = d.length === 11 && d.startsWith("1") ? d.slice(1) : d;
  return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : p;
};

// S14: distances for people — feet under a quarter mile, miles above.
export const fmtDistFt = (ft) => {
  const n = Number(ft);
  if (!Number.isFinite(n)) return "";
  return n >= 1320 ? `${(n / 5280).toFixed(1)} mi` : `${Math.round(n)} ft`;
};

// S4: format-as-you-type for phone inputs (US).
export const fmtPhoneInput = (v) => {
  let d = String(v || "").replace(/\D/g, "");
  if (d.startsWith("1")) d = d.slice(1);
  d = d.slice(0, 10);
  if (d.length > 6) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
  if (d.length > 3) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return d.length ? `(${d}` : "";
};

// Display-only casing fix: capitalizes surname-style tokens stored
// all-lowercase ("James knepp" -> "James Knepp") while leaving connectors
// alone ("Mike and Sarah Johnson" stays as-is). Never rewrites stored data.
const NAME_STOPWORDS = new Set(["and", "de", "del", "la", "le", "van", "von", "der", "den", "y", "e", "di", "da"]);
export const displayName = (n) =>
  String(n || "").replace(/(^|[\s'-])([a-zà-ÿ]{2,})/g, (m, sep, w) =>
    NAME_STOPWORDS.has(w) ? m : sep + w[0].toUpperCase() + w.slice(1));

// One date helper for routine views: "Sep 26" (same year) or "Sep 26, 2026".
export const fmtDay = (d) => {
  const dt = d instanceof Date ? d : (typeof d === "string" && d.length === 10 ? parseLocalDay(d) : safeDate(d));
  if (!dt) return "";
  const sameYear = dt.getFullYear() === new Date().getFullYear();
  return dt.toLocaleDateString(locale(), sameYear ? { month: "short", day: "numeric" } : { year: "numeric", month: "short", day: "numeric" });
};

// User-facing timestamp with an explicit timezone label and no seconds:
// "Sep 26, 2026 · 4:41 AM EDT". Use wherever a bare local time could be
// misread (activity feeds, logs, assistant history).
export const fmtWhen = (d) => {
  const dt = safeDate(d);
  if (!dt) return "";
  const date = dt.toLocaleDateString(locale(), { year: "numeric", month: "short", day: "numeric" });
  const time = dt.toLocaleTimeString(locale(), { hour: "numeric", minute: "2-digit", timeZoneName: "short" });
  return `${date} · ${time}`;
};
